import {
  Stepper,
  Step,
  StepLabel,
  Stack,
  Button,
  Typography,
  useTheme,
} from "@mui/material";
import router from "next/router";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { PageContainer } from "../../../components/Layout/PageContainer";
import OrderDetails from "../../../components/checkout/OrderDetails";
import { useShopContext } from "../../../context/shop/shop.context";
import RecapStep from "./RecapStep";
import ShippingStep from "./ShippingStep";
import { useShippingForm } from "./useShippingForm";
import { getLogger } from "../../../utils/logger/pino";

const logger = getLogger("Checkou page");

export interface CheckoutStepProps {
  handlePrev: () => void;
  handleNext: () => void;
}

const Checkout = () => {
  const { t } = useTranslation();
  const { shopState } = useShopContext();
  const theme = useTheme();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const shippingForm = useShippingForm(() => setActiveStep((step) => step + 1));
  const [errorMessage, setErrorMessage] = useState<string>();
  const [waitingMessage, setWaitingMessage] = useState<string>();
  const handleOrder = async () => {
    setLoading(true);
    setTimeout(
      () => setWaitingMessage(t("page_checkout.waiting_message") ?? ""),
      7000
    );
    setErrorMessage(undefined);
    try {
      const data = await fetch("/api/shop/checkout", {
        method: "POST",
        body: JSON.stringify({ shopState }),
      });
      const { url } = await data.json();
      window.location = url;
    } catch (e) {
      setLoading(false);
      setWaitingMessage(undefined);
      setErrorMessage(t("page_checkout.shipping.error_checkout") ?? "Error");
      logger.error("Error: ", e);
    }
  };
  const steps = [
    {
      title: t("page_checkout.steps.0.title"),
      component: <ShippingStep shippingForm={shippingForm} />,
      action: shippingForm.onSubmit,
      actionLabel: t("page_checkout.steps.0.action"),
    },
    {
      title: t("page_checkout.steps.1.title"),
      component: (
        <RecapStep handlePrev={() => setActiveStep((step) => step - 1)} />
      ),
      action: handleOrder,
      actionLabel: t("page_checkout.steps.1.action"),
    },
  ];
  return (
    <PageContainer>
      <Stack
        sx={{
          background: theme.palette.background.layout,
          width: "100%",
          py: 2,
          mb: 2,
        }}
      >
        <Typography variant="h3" align="center">
          {t("page_checkout.title")}
        </Typography>
      </Stack>
      {shopState.items.length > 0 ? (
        <Stack
          sx={{
            width: "100%",
            maxWidth: "lg",
            alignItems: "center",
          }}
        >
          <Stack flexDirection="row" alignSelf="stretch" sx={{ my: 1 }}>
            <Stack sx={{ flex: 1, alignItems: "flex-start" }}>
              <Button
                onClick={() => router.push("/shop/cart")}
                sx={{ display: { md: "block", xs: "none" } }}
              >
                {t("page_checkout.back")}
              </Button>
            </Stack>
            <Stepper sx={{ flex: 1 }} activeStep={activeStep}>
              {steps.map((step) => {
                return (
                  <Step key={step.title}>
                    <StepLabel>{step.title}</StepLabel>
                  </Step>
                );
              })}
            </Stepper>
            <Stack sx={{ flex: 1 }} />
          </Stack>
          <Stack
            flexDirection={{ md: "row", xs: "column-reverse" }}
            alignSelf="stretch"
          >
            <Stack flex={3} p={2}>
              {steps[activeStep].component}
            </Stack>
            <Stack flex={2} p={2}>
              <OrderDetails
                loading={loading}
                checkoutStep
                waitingMessage={waitingMessage}
                errorMessage={errorMessage}
                showShipping={activeStep == 1}
                onClickNext={steps[activeStep].action}
                nextLabel={steps[activeStep].actionLabel}
              />
            </Stack>
          </Stack>
        </Stack>
      ) : (
        <>
          <Typography variant="h4" alignSelf="center" mt={5}>
            {t("page_checkout.empty")}
          </Typography>
          <Button
            variant="contained"
            sx={{ mt: 5 }}
            onClick={() => router.push("/shop")}
          >
            {t("page_checkout.button")}
          </Button>
        </>
      )}
    </PageContainer>
  );
};

export default Checkout;
